import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { toast } from 'react-toastify';
import Cropper from 'react-easy-crop';
import { API_URL } from '../config/api';
import { setUser } from '../store/userSlice';
import LoadingIndicator from '../Components/LoadingIndicator';

const Profile = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);

  const displayName = user.name || 'User';
  const initial = displayName.trim().charAt(0).toUpperCase();
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [cropImage, setCropImage] = useState(null);
  const [isCropOpen, setIsCropOpen] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [formData, setFormData] = useState({
    name: user.name || '',
    phone: user.phone || '',
    password: ''
  });

  const passwordRules = [
  {
    label: "Minimum 8 characters",
    test: (password = '') => password.length >= 8
  },
  {
    label: "One uppercase letter",
    test: (password = '') => /[A-Z]/.test(password)
  },
  {
    label: "One lowercase letter",
    test: (password = '') => /[a-z]/.test(password)
  },
  {
    label: "One number",
    test: (password = '') => /[0-9]/.test(password)
  },
  {
    label: "One special character",
    test: (password = '') => /[!@#$%^&*]/.test(password)
  }
];


const isPasswordValid = (password = '') => {
  return passwordRules.every(rule => rule.test(password));
};

  const storageUpdate = useMemo(() => ({
    name: user.name,
    email: user.email,
    role: user.role,
    phone: user.phone,
    companyName: user.companyName,
    gstNumber: user.gstNumber,
    profileImage: user.profileImage,
    profileImageKey: user.profileImageKey
  }), [user]);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!API_URL || !user.userId) return;

      try {
        const response = await axios.post(API_URL, {
          action: 'getProfile',
          userId: user.userId
        }, {
          headers: { 'Content-Type': 'application/json' }
        });

        const profile = response.data || {};

        const merged = {
          userId: user.userId,
          name: profile.name ?? user.name,
          email: profile.email ?? user.email,
          role: profile.role ?? user.role,
          phone: profile.phone ?? user.phone,
          companyName: profile.companyname ?? user.companyName,
          gstNumber: profile.gst ?? user.gstNumber,
          profileImageKey: profile.profileImageUrl || user.profileImageKey || null,
          profileImage: user.profileImage
        };

        localStorage.setItem('name', merged.name || '');
        localStorage.setItem('email', merged.email || '');
        localStorage.setItem('role', merged.role || '');
        localStorage.setItem('phone', merged.phone || '');
        localStorage.setItem('companyName', merged.companyName || '');
        localStorage.setItem('gstNumber', merged.gstNumber || '');
        if (merged.profileImageKey) {
          localStorage.setItem('profileImageKey', merged.profileImageKey);
        }

        dispatch(setUser(merged));
      } catch (error) {
        console.error('Profile fetch error:', error);
      }
    };

    fetchProfile();
  }, [dispatch, user.userId, user.email]);

  useEffect(() => {
    setFormData({
      name: user.name || '',
      phone: user.phone || ''
    });
  }, [user.name, user.phone]);

  useEffect(() => {
    const fetchProfileImageUrl = async () => {
      if (!API_URL || !user.userId || !user.profileImageKey) return;

      try {
        const response = await axios.post(API_URL, {
          action: 'getProfileImageUrl',
          userId: user.userId
        }, {
          headers: { 'Content-Type': 'application/json' }
        });

        const profileImageUrl = response.data?.profileImageUrl;
        if (profileImageUrl) {
          localStorage.setItem('profileImage', profileImageUrl);
          dispatch(setUser({ ...storageUpdate, userId: user.userId, profileImage: profileImageUrl }));
        }
      } catch (error) {
        console.error('Profile image fetch error:', error);
      }
    };

    fetchProfileImageUrl();
  }, [dispatch, user.userId, user.profileImageKey, storageUpdate]);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setCropImage(reader.result);
      setIsCropOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const onCropComplete = (_croppedArea, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  };

  const getCroppedBlob = async () => {
    if (!cropImage || !croppedAreaPixels) return null;
    const image = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = cropImage;
    });

    const canvas = document.createElement('canvas');
    canvas.width = croppedAreaPixels.width;
    canvas.height = croppedAreaPixels.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(
      image,
      croppedAreaPixels.x,
      croppedAreaPixels.y,
      croppedAreaPixels.width,
      croppedAreaPixels.height,
      0,
      0,
      croppedAreaPixels.width,
      croppedAreaPixels.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.92);
    });
  };

  const handleCropSave = async () => {
    const blob = await getCroppedBlob();
    if (!blob) {
      toast.error('Unable to crop image.');
      return;
    }
    const file = new File([blob], 'profile.jpg', { type: 'image/jpeg' });
    setSelectedFile(file);
    setIsCropOpen(false);
    await updateProfileData(file);
  };

  const handleCropCancel = () => {
    setIsCropOpen(false);
    setCropImage(null);
    setCroppedAreaPixels(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const updateProfileData = async (fileOverride) => {
    if (!API_URL || !user.userId) {
      toast.error('API URL not configured.');
      return;
    }
    if (!formData.name.trim() || !formData.phone.trim()) {
      toast.error('Name and phone are required.');
      return;
    }
    if(showPasswordChange){

  if(!formData.password || !confirmPassword){
    setPasswordError(
      "Please enter password and confirm password"
    );
    return;
  }

  if(formData.password !== confirmPassword){
    setPasswordError(
      "Passwords do not match"
    );
    return;
  }
 
  if(!isPasswordValid(formData.password)){
    setPasswordError(
      "Password does not meet requirements"
    );
    return;
  }

}

    setIsUploading(true);
    try {
      const fileToUpload = fileOverride || selectedFile;
      const payload = {
        action: 'updateProfile',
        userId: user.userId,
        name: formData.name.trim(),
        phone: formData.phone.trim(),
         ...(showPasswordChange && {
    password: formData.password.trim()
  })
      };

      if (fileToUpload) {
        payload.profileImageFileName = fileToUpload.name;
        payload.profileImageContentType = fileToUpload.type || 'application/octet-stream';
      }

      const updateResponse = await axios.post(API_URL, payload, {
        headers: { 'Content-Type': 'application/json' }
      });

      const uploadUrl = updateResponse.data?.uploadUrl;
      const profileImageKey = updateResponse.data?.profileImageKey;

      if (fileToUpload && uploadUrl) {
        await axios.put(uploadUrl, fileToUpload, {
          headers: { 'Content-Type': fileToUpload.type || 'application/octet-stream' }
        });
      }

      if (profileImageKey) {
        localStorage.setItem('profileImageKey', profileImageKey);
        dispatch(setUser({ ...storageUpdate, userId: user.userId, profileImageKey }));
      }

      if (fileToUpload) {
        const viewResponse = await axios.post(API_URL, {
          action: 'getProfileImageUrl',
          userId: user.userId
        }, {
          headers: { 'Content-Type': 'application/json' }
        });

        const profileImageUrl = viewResponse.data?.profileImageUrl;
        if (profileImageUrl) {
          localStorage.setItem('profileImage', profileImageUrl);
          dispatch(setUser({ ...storageUpdate, userId: user.userId, profileImage: profileImageUrl }));
        }
      }

      localStorage.setItem('name', formData.name.trim());
      localStorage.setItem('phone', formData.phone.trim());
      dispatch(setUser({
        ...storageUpdate,
        userId: user.userId,
        name: formData.name.trim(),
        phone: formData.phone.trim()
      }));

      toast.success(updateResponse.data?.message || 'Profile updated successfully');
      setShowPasswordChange(false);

setFormData((prev) => ({
  ...prev,
  password: ''
}));

setConfirmPassword('');
setPasswordError('');
setShowPassword(false);
setShowConfirmPassword(false);

      setSelectedFile(null);
      setCropImage(null);
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Update failed';
      toast.error(message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSave = async () => {
    await updateProfileData();
  };

  return (
    <div className="employee-form">
      <div className="content-header">
        <div className="row align-items-center">
          <div className="col-sm-6">
            <h1>Profile</h1>
            <p>Review your profile details and update your photo.</p>
          </div>
        </div>
      </div>

      <div className="card employee-content-card profile-card">
        <div className="card-body">
          <div className="row align-items-center">
            <div className="col-md-3 text-center">
            <div className={`profile-avatar ${isUploading ? 'is-loading' : ''}`}>
              {user.profileImage ? (
                <img src={user.profileImage} alt="Profile" />
              ) : (
                <span>{initial || 'U'}</span>
              )}
              {isUploading && (
                <div className="profile-avatar-loader">
                  <LoadingIndicator size={60} />
                </div>
              )}
            </div>
              <h5 className="profile-name">{displayName}</h5>
              <p className="profile-role">{user.role || 'Member'}</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="d-none"
                onChange={handleFileChange}
              />
              <button
                type="button"
                className="btn btn-primary btn-sm profile-upload-btn"
                onClick={handleUploadClick}
                disabled={isUploading}
              >
                {selectedFile ? 'Change Photo' : 'Upload Photo'}
              </button>
            </div>
            <div className="col-md-9">
              <div className="profile-info-grid">
                <div className="form-group">
                  <label className="profile-label" htmlFor="profile-name">Name</label>
                  <input
                    id="profile-name"
                    name="name"
                    className="form-control"
                    value={formData.name}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label className="profile-label" htmlFor="profile-email">Email</label>
                  <input
                    id="profile-email"
                    className="form-control"
                    value={user.email || ''}
                    readOnly
                  />
                </div>
                <div className="form-group">
                  <label className="profile-label" htmlFor="profile-phone">Phone</label>
                  <input
                    id="profile-phone"
                    name="phone"
                    className="form-control"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label className="profile-label">Role</label>
                  <input
                    className="form-control"
                    value={user.role || ''}
                    readOnly
                  />
                </div>
              </div>
             <div className="form-group mt-4">
                <label className="profile-label">Password</label>

                <div>
                  <a
                    href="#"
                    className="text-primary"
                    onClick={(e) => {
                      e.preventDefault();

                      setShowPasswordChange(!showPasswordChange);

                      if (showPasswordChange) {
                        setFormData({
                          ...formData,
                          password: "",
                        });
                        setConfirmPassword("");
                        setPasswordError("");
                        setShowPassword(false);
                        setShowConfirmPassword(false);
                      }
                    }}
                  >
                    {showPasswordChange
                      ? "Cancel Password Change"
                      : "Change Password"}
                  </a>
                </div>

                {showPasswordChange && (
                  <div
                    className="mt-3"
                    style={{
                      maxWidth: "430px",
                    }}
                  >
                    {/* New Password */}

                    <label>New Password</label>

                    <div className="position-relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        className="form-control"
                        placeholder="Enter new password"
                        value={formData.password}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            password: e.target.value,
                          });

                          setPasswordError("");
                        }}
                        style={{
                          paddingRight: "40px",
                        }}
                      />

                      <span
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                          position: "absolute",
                          right: "12px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          cursor: "pointer",
                        }}
                      >
                        <i
                          className={`fas ${
                            showPassword ? "fa-eye-slash" : "fa-eye"
                          }`}
                        />
                      </span>
                    </div>

                    {/* Rules */}

                    {formData.password && (
                      <ul className="mt-2">
                        {passwordRules.map((rule, index) => (
                          <li
                            key={index}
                            style={{
                              color: rule.test(formData.password)
                                ? "green"
                                : "gray",
                            }}
                          >
                            {rule.test(formData.password) ? "✓" : "○"}
                            &nbsp;
                            {rule.label}
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Confirm Password */}

                    <label className="mt-2">Confirm Password</label>

                    <div className="position-relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        className="form-control"
                        placeholder="Confirm password"
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value);
                          setPasswordError("");
                        }}
                        style={{
                          paddingRight: "40px",
                        }}
                      />

                      <span
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        style={{
                          position: "absolute",
                          right: "12px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          cursor: "pointer",
                        }}
                      >
                        <i
                          className={`fas ${
                            showConfirmPassword ? "fa-eye-slash" : "fa-eye"
                          }`}
                        />
                      </span>
                    </div>

                    {confirmPassword && (
                      <small
                        className={
                          formData.password === confirmPassword
                            ? "text-success"
                            : "text-danger"
                        }
                      >
                        {formData.password === confirmPassword
                          ? "✓ Passwords match"
                          : "✕ Passwords do not match"}
                      </small>
                    )}

                    {passwordError && (
                      <div className="text-danger mt-2">{passwordError}</div>
                    )}
                  </div>
                )}
              </div>
              <div className="form-actions" style={{ textAlign: "center" }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={isUploading}
                >
                  {isUploading ? (
                    <LoadingIndicator size={22} className="inline-loader" />
                  ) : (
                    "Save Changes"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {isCropOpen && (
        <div className="crop-overlay">
          <div className="crop-modal">
            <div className="crop-header">
              <h5>Crop Profile Image</h5>
              <button type="button" className="crop-close" onClick={handleCropCancel}>
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            <div className="crop-body">
              <div className="crop-container">
                <Cropper
                  image={cropImage}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  cropShape="round"
                  showGrid={false}
                />
              </div>
              <div className="crop-controls">
                <span>Zoom</span>
                <input
                  type="range"
                  min="1"
                  max="3"
                  step="0.1"
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                />
              </div>
            </div>
            <div className="crop-footer">
              <button type="button" className="btn btn-secondary" onClick={handleCropCancel}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={handleCropSave}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;